/**
 * schema-dts 类型安全的 JSON-LD 结构化数据工具
 * @see https://developers.google.com/search/docs/advanced/structured-data/intro-structured-data
 */
import type { TypedObject } from '@portabletext/types';
import type {
  BlogPosting,
  BreadcrumbList,
  FAQPage,
  IdReference,
  ImageObject,
  ItemList,
  ListItem,
  Offer,
  Organization,
  Question,
  SoftwareApplication,
  Thing,
  WebPage,
  WithContext,
} from 'schema-dts';
import { LANGUAGE_CONFIG } from '@/i18n/config';

const SITE_ORIGIN = 'https://www.vidu.com';
const ORG_ID = `${SITE_ORIGIN}/#organization`;
const WEBSITE_ID = `${SITE_ORIGIN}/#website`;
const SITE_SOFTWARE_ID = `${SITE_ORIGIN}/#software`;

type CustomGraphItem = Record<string, unknown> & {
  '@type': string | string[];
  '@id'?: string;
};

export type JsonLdGraphItem = Thing | IdReference | CustomGraphItem;

export type JsonLdGraphContent = {
  '@context': 'https://schema.org';
  '@graph': JsonLdGraphItem[];
};

type WebPageMainEntity =
  | {
      type: 'TermsOfService';
      idSuffix?: string;
    }
  | {
      type: 'Article';
      idSuffix?: string;
    }
  | {
      type: 'VideoObject';
      idSuffix?: string;
      embedUrl?: string;
    };

type PageSoftwareApplicationOptions = {
  idSuffix?: string;
  applicationCategory?: string;
  operatingSystem?: string;
  isAccessibleForFree?: boolean;
  offers?: { price: string; priceCurrency?: string };
};

function idRef(id: string): IdReference {
  return { '@id': id };
}

/**
 * 把 site-relative path 拼成 locale-aware 绝对 URL，与 generateCanonicalMetadata 行为保持一致：
 * 默认 locale 不带前缀，其它 locale 带 `/locale` 前缀。
 */
function buildPageUrl(path: string, locale?: string): string {
  const normalizedPath = path === '/' ? '' : path;
  const useLocalePrefix = locale && locale !== LANGUAGE_CONFIG.default;
  return `${SITE_ORIGIN}${useLocalePrefix ? `/${locale}` : ''}${normalizedPath}` || `${SITE_ORIGIN}/`;
}

type BreadcrumbItem = { name: string; path?: string };

function buildBreadcrumbList(items: BreadcrumbItem[], locale?: string): WithContext<BreadcrumbList> {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map(
      (item, index): ListItem => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        ...(item.path && { item: buildPageUrl(item.path, locale) }),
      }),
    ),
  };
}

/**
 * FAQPage 结构化数据
 */
export function createFAQPageSchema(params: {
  questions: Array<{
    question: string;
    answer: string;
  }>;
}): WithContext<FAQPage> {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: params.questions.map(
      (item): Question => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.answer,
        },
      }),
    ),
  };
}

/**
 * 从 Portable Text 块中提取纯文本
 */
export function extractTextFromPortableText(blocks: TypedObject[] | TypedObject): string {
  if (!blocks) return '';

  const blocksArray = Array.isArray(blocks) ? blocks : [blocks];

  return blocksArray
    .map((block) => {
      if (
        typeof block === 'object' &&
        block !== null &&
        '_type' in block &&
        block._type === 'block' &&
        'children' in block &&
        Array.isArray(block.children)
      ) {
        return block.children
          .map((child) => {
            if (typeof child === 'object' && child !== null && 'text' in child) {
              return child.text || '';
            }
            return '';
          })
          .join(' ');
      }
      return '';
    })
    .filter(Boolean)
    .join(' ');
}

/**
 * SoftwareApplication 结构化数据（用于 workspace 工具页面）
 */
export function createSoftwareApplicationSchema(params: {
  name: string;
  description: string;
  url: string;
  imageUrl?: string;
  applicationCategory?: string;
  operatingSystem?: string;
  datePublished?: string;
  dateModified?: string;
  publisherName?: string;
  publisherLogoUrl?: string;
  price?: string;
  priceCurrency?: string;
}): WithContext<SoftwareApplication> {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: params.name,
    description: params.description,
    url: params.url,
    ...(params.imageUrl && {
      image: {
        '@type': 'ImageObject',
        url: params.imageUrl,
      } as ImageObject,
    }),
    ...(params.applicationCategory && { applicationCategory: params.applicationCategory }),
    ...(params.operatingSystem && { operatingSystem: params.operatingSystem }),
    ...(params.datePublished && { datePublished: params.datePublished }),
    ...(params.dateModified && { dateModified: params.dateModified }),
    ...(params.publisherName && {
      publisher: {
        '@type': 'Organization',
        name: params.publisherName,
        ...(params.publisherLogoUrl && {
          logo: {
            '@type': 'ImageObject',
            url: params.publisherLogoUrl,
          } as ImageObject,
        }),
      } as Organization,
    }),
    ...(params.price !== undefined && {
      offers: {
        '@type': 'Offer',
        price: params.price,
        ...(params.priceCurrency && { priceCurrency: params.priceCurrency }),
        availability: 'https://schema.org/InStock',
      } as Offer,
    }),
  };
}

/**
 * BreadcrumbList 结构化数据（独立使用时）
 * @see https://developers.google.com/search/docs/appearance/structured-data/breadcrumb
 */
export function createBreadcrumbListSchema(items: BreadcrumbItem[], locale?: string): WithContext<BreadcrumbList> {
  return buildBreadcrumbList(items, locale);
}

/**
 * 产品页 @graph 结构化数据
 * 单个 JSON-LD 块包含 WebPage + SoftwareApplication + BreadcrumbList
 *
 * - URL / @id 全部按 locale 拼接，与 generateCanonicalMetadata 保持一致；
 * - SoftwareApplication 通过 isPartOf 关联全局 #software，避免站点级与页面级两个独立 SoftwareApplication 实体并存；
 * - offers 由调用方按真实定价传入，不再默认 free。
 */
export function createProductPageGraph(params: {
  name: string;
  description: string;
  path: string;
  locale?: string;
  imageUrl?: string;
  datePublished?: string;
  applicationCategory?: string;
  offers?: { price: string; priceCurrency?: string };
  breadcrumbs: BreadcrumbItem[];
}): JsonLdGraphContent {
  const pageUrl = buildPageUrl(params.path, params.locale);
  const webpageId = `${pageUrl}#webpage`;
  const softwareId = `${pageUrl}#software`;

  const webpage: WithContext<WebPage> = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': webpageId,
    url: pageUrl,
    name: params.name,
    description: params.description,
    isPartOf: idRef(WEBSITE_ID),
    ...(params.locale && { inLanguage: params.locale }),
    mainEntity: idRef(softwareId),
  };

  const software: WithContext<SoftwareApplication> = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': softwareId,
    name: params.name,
    description: params.description,
    url: pageUrl,
    applicationCategory: params.applicationCategory || 'MultimediaApplication',
    operatingSystem: 'Web',
    isPartOf: idRef(SITE_SOFTWARE_ID),
    ...(params.imageUrl && {
      image: { '@type': 'ImageObject', url: params.imageUrl } as ImageObject,
    }),
    ...(params.datePublished && { datePublished: params.datePublished }),
    ...(params.offers && {
      offers: {
        '@type': 'Offer',
        price: params.offers.price,
        ...(params.offers.priceCurrency && { priceCurrency: params.offers.priceCurrency }),
      } as Offer,
    }),
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [webpage, software, buildBreadcrumbList(params.breadcrumbs, params.locale)],
  };
}

/**
 * Blog 文章页面 @graph 结构化数据
 * 单个 JSON-LD 块包含 WebPage + BlogPosting + BreadcrumbList，Organization 通过 @id 引用站点级实体。
 */
export function createBlogPostPageGraph(params: {
  title: string;
  description: string;
  slug: string;
  locale?: string;
  datePublished: string;
  dateModified?: string;
  authorName: string;
  imageUrl?: string;
  imageWidth?: number;
  imageHeight?: number;
  breadcrumbs: BreadcrumbItem[];
}): JsonLdGraphContent {
  const pageUrl = buildPageUrl(`/blog/${params.slug}`, params.locale);
  const webpageId = `${pageUrl}#webpage`;
  const articleId = `${pageUrl}#article`;

  const webpage: WithContext<WebPage> = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': webpageId,
    url: pageUrl,
    name: params.title,
    description: params.description,
    isPartOf: idRef(WEBSITE_ID),
    ...(params.locale && { inLanguage: params.locale }),
    mainEntity: idRef(articleId),
  };

  const article: WithContext<BlogPosting> = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    '@id': articleId,
    headline: params.title,
    description: params.description,
    mainEntityOfPage: idRef(webpageId),
    datePublished: params.datePublished,
    ...(params.dateModified && { dateModified: params.dateModified }),
    author: { '@type': 'Person', name: params.authorName },
    publisher: idRef(ORG_ID),
    ...(params.imageUrl && {
      image: {
        '@type': 'ImageObject',
        url: params.imageUrl,
        ...(params.imageWidth && { width: params.imageWidth }),
        ...(params.imageHeight && { height: params.imageHeight }),
      } as ImageObject,
    }),
  };

  return {
    '@context': 'https://schema.org',
    '@graph': [webpage, article, buildBreadcrumbList(params.breadcrumbs, params.locale)],
  };
}

/**
 * CollectionPage @graph — 用于列表/聚合页（tools list、blog list、blog category 等）
 * 可选传入 itemList，让聚合页结构化数据描述出实际的子项。
 */
export function createCollectionPageGraph(params: {
  name: string;
  description: string;
  path: string;
  locale?: string;
  pageTypes?: Array<'WebPage' | 'CollectionPage' | 'Blog'>;
  breadcrumbs: BreadcrumbItem[];
  itemList?: Array<{ name: string; path: string }>;
  softwareApplication?: PageSoftwareApplicationOptions;
}): JsonLdGraphContent {
  const pageUrl = buildPageUrl(params.path, params.locale);
  const webpageId = `${pageUrl}#webpage`;
  const itemListId = `${pageUrl}#itemlist`;
  const softwareId = params.softwareApplication
    ? `${pageUrl}#${params.softwareApplication.idSuffix ?? 'software'}`
    : undefined;

  const collectionPage: CustomGraphItem = {
    '@context': 'https://schema.org',
    '@type': params.pageTypes ?? 'CollectionPage',
    '@id': webpageId,
    url: pageUrl,
    name: params.name,
    description: params.description,
    isPartOf: idRef(WEBSITE_ID),
    ...(params.locale && { inLanguage: params.locale }),
    ...(softwareId && { mainEntity: idRef(softwareId) }),
    ...(params.itemList && params.itemList.length > 0 && { mainEntity: idRef(itemListId) }),
  };

  const graph: JsonLdGraphItem[] = [collectionPage];

  if (params.softwareApplication && softwareId) {
    graph.push({
      '@type': 'SoftwareApplication',
      '@id': softwareId,
      name: params.name,
      url: pageUrl,
      description: params.description,
      applicationCategory: params.softwareApplication.applicationCategory ?? 'MultimediaApplication',
      operatingSystem: params.softwareApplication.operatingSystem ?? 'Web',
      ...(params.softwareApplication.isAccessibleForFree !== undefined && {
        isAccessibleForFree: params.softwareApplication.isAccessibleForFree,
      }),
      ...(params.softwareApplication.offers && {
        offers: {
          '@type': 'Offer',
          price: params.softwareApplication.offers.price,
          ...(params.softwareApplication.offers.priceCurrency && {
            priceCurrency: params.softwareApplication.offers.priceCurrency,
          }),
        },
      }),
    });
  }

  if (params.itemList && params.itemList.length > 0) {
    const itemList: WithContext<ItemList> = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      '@id': itemListId,
      itemListElement: params.itemList.map(
        (entry, index): ListItem => ({
          '@type': 'ListItem',
          position: index + 1,
          name: entry.name,
          url: buildPageUrl(entry.path, params.locale),
        }),
      ),
    };
    graph.push(itemList);
  }

  graph.push(buildBreadcrumbList(params.breadcrumbs, params.locale));

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

/**
 * 通用 WebPage @graph — 用于不属于产品/列表/博客的 SEO 页面（help-center、活动落地页等）。
 * 不强制 SoftwareApplication 语义。
 */
export function createWebPageGraph(params: {
  name: string;
  description: string;
  path: string;
  locale?: string;
  pageType?: 'WebPage' | 'AboutPage' | 'ContactPage' | 'CollectionPage';
  breadcrumbs?: BreadcrumbItem[];
  mainEntity?: WebPageMainEntity;
  softwareApplication?: PageSoftwareApplicationOptions;
  /**
   * 设为 true 时，WebPage.about 关联到全局 SoftwareApplication（站点级），
   * 适合首页这类「介绍站点/产品」的页面。
   */
  aboutSiteSoftware?: boolean;
}): JsonLdGraphContent {
  const pageUrl = buildPageUrl(params.path, params.locale);
  const pageIdBase = params.path === '/' ? pageUrl.replace(/\/$/, '') : pageUrl;
  const webpageId = `${pageIdBase}#webpage`;
  const mainEntityId = params.mainEntity ? `${pageUrl}#${params.mainEntity.idSuffix ?? 'mainEntity'}` : undefined;
  const softwareId = params.softwareApplication
    ? `${pageIdBase}#${params.softwareApplication.idSuffix ?? 'software'}`
    : undefined;

  const webpage: WithContext<WebPage> = {
    '@context': 'https://schema.org',
    '@type': params.pageType ?? 'WebPage',
    '@id': webpageId,
    url: pageUrl,
    name: params.name,
    description: params.description,
    isPartOf: idRef(WEBSITE_ID),
    ...(params.aboutSiteSoftware && { about: idRef(SITE_SOFTWARE_ID) }),
    ...(mainEntityId && { mainEntity: idRef(mainEntityId) }),
    ...(softwareId && { mainEntity: idRef(softwareId) }),
    ...(params.locale && { inLanguage: params.locale }),
  };

  const graph: JsonLdGraphContent['@graph'] = [webpage];
  if (params.softwareApplication && softwareId) {
    graph.push({
      '@type': 'SoftwareApplication',
      '@id': softwareId,
      name: params.name,
      url: pageUrl,
      description: params.description,
      applicationCategory: params.softwareApplication.applicationCategory ?? 'MultimediaApplication',
      operatingSystem: params.softwareApplication.operatingSystem ?? 'Web',
      ...(params.softwareApplication.isAccessibleForFree !== undefined && {
        isAccessibleForFree: params.softwareApplication.isAccessibleForFree,
      }),
      ...(params.softwareApplication.offers && {
        offers: {
          '@type': 'Offer',
          price: params.softwareApplication.offers.price,
          ...(params.softwareApplication.offers.priceCurrency && {
            priceCurrency: params.softwareApplication.offers.priceCurrency,
          }),
        },
      }),
    });
  }
  if (params.mainEntity && mainEntityId) {
    const mainEntity: CustomGraphItem = {
      '@type': params.mainEntity.type,
      '@id': mainEntityId,
      name: params.name,
      description: params.description,
      url: pageUrl,
      mainEntityOfPage: idRef(webpageId),
    };

    if (params.mainEntity.type === 'Article') {
      mainEntity.headline = params.name;
      mainEntity.author = { '@type': 'Organization', name: 'Vidu AI' };
      mainEntity.publisher = idRef(ORG_ID);
    }

    if (params.mainEntity.type === 'VideoObject') {
      mainEntity.embedUrl = params.mainEntity.embedUrl ?? pageUrl;
      mainEntity.publisher = idRef(ORG_ID);
    }

    graph.push(mainEntity);
  }
  if (params.breadcrumbs && params.breadcrumbs.length > 0) {
    graph.push(buildBreadcrumbList(params.breadcrumbs, params.locale));
  }

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}
